import { useCallback, useMemo, useState } from 'react';
import { getSongTiktokAudioPostStats, GetSongTiktokAudioPostStatsParams, usePaginatedFetch } from '@round/api';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import { TopTiktokAudioPostsTableProps } from './TopTiktokAudioPosts';

type UseTopTiktokAudioPostsReturn = {
    refresh: (requestInit?: RequestInit) => Promise<void>;
    getTopTiktokAudioPostsTableProps: () => TopTiktokAudioPostsTableProps;
};

export default function useTopTiktokAudioPosts(songId: number | null): UseTopTiktokAudioPostsReturn {
    const fetchSongTiktokAudioPostStats = useCallback(
        async (params: GetSongTiktokAudioPostStatsParams, requestInit?: RequestInit) => {
            if (!songId) {
                throw new Error('No song passed');
            }

            return getSongTiktokAudioPostStats(songId, params, requestInit);
        },
        [songId]
    );

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');

    const {
        count,
        loading,
        page: stats,
        getCurrentPage,
    } = usePaginatedFetch(fetchSongTiktokAudioPostStats, {
        search,
        page,
        page_size: pageSize,
        ordering: '-play_count',
    });

    useAbortableEffect(
        (signal) => {
            if (typeof songId === 'number') {
                getCurrentPage({ signal });
            }
        },
        [getCurrentPage, songId]
    );

    return useMemo(
        () => ({
            refresh: getCurrentPage,
            getTopTiktokAudioPostsTableProps: () => ({
                rows: stats,
                loading,
                page,
                setPageSize: (pageSize: number | undefined) => setPageSize(pageSize ?? 10),
                setPage,
                pageSize,
                setSearch: (search: string) => {
                    setPage(1);
                    setSearch(search);
                },
                search,
                count,
            }),
        }),
        [count, getCurrentPage, loading, page, pageSize, search, stats]
    );
}
